import React, { useState, useEffect, useCallback } from 'react';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { toast } from '../components/Toast';
import { formatCurrency } from '../utils/currency';
import config from '../config';
import './FinancialTransactions.css';

const formatDate = (d) =>
  new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const MODE_LABELS = { CASH: 'Cash', BANK: 'Bank Transfer', UPI: 'UPI', CHEQUE: 'Cheque' };

function printReceipt(txn, logoUrl) {
  const logoHtml = logoUrl
    ? `<img src="${logoUrl}" alt="logo" style="height:60px;max-width:160px;object-fit:contain;display:block;margin:0 auto 8px;" />`
    : '';

  const html = `
    <html><head><title>Receipt - ${txn.voucherNo}</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 30px; max-width: 400px; margin: 0 auto; }
      .receipt-header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 14px; margin-bottom: 18px; }
      .receipt-header h2 { margin: 0 0 4px; font-size: 20px; }
      .receipt-header p { margin: 0; color: #666; font-size: 13px; }
      .voucher { text-align: center; background: #f5f5f5; padding: 8px; border-radius: 6px; font-family: monospace; font-size: 16px; font-weight: bold; margin-bottom: 18px; letter-spacing: 1px; }
      table { width: 100%; border-collapse: collapse; }
      td { padding: 8px 4px; font-size: 13px; border-bottom: 1px solid #eee; }
      td:first-child { color: #666; width: 40%; }
      td:last-child { font-weight: 500; text-align: right; }
      .amount-row td { font-size: 18px; font-weight: bold; border-top: 2px solid #333; border-bottom: none; padding-top: 12px; }
      .footer { text-align: center; margin-top: 24px; color: #999; font-size: 11px; border-top: 1px solid #ddd; padding-top: 12px; }
    </style></head>
    <body>
      <div class="receipt-header">
        ${logoHtml}
        <h2>Payment Receipt</h2>
        <p>Official Receipt</p>
      </div>
      <div class="voucher">${txn.voucherNo}</div>
      <table>
        <tr><td>Date</td><td>${formatDate(txn.transactionDate)}</td></tr>
        <tr><td>Customer</td><td>${txn.customerName || '—'}</td></tr>
        ${txn.projectName ? `<tr><td>Project</td><td>${txn.projectName}</td></tr>` : ''}
        ${txn.accountHeadName ? `<tr><td>Type</td><td>${txn.accountHeadName}</td></tr>` : ''}
        <tr><td>Payment Mode</td><td>${MODE_LABELS[txn.paymentMode] || txn.paymentMode || '—'}</td></tr>
        ${txn.referenceNo ? `<tr><td>Reference</td><td>${txn.referenceNo}</td></tr>` : ''}
        ${txn.notes ? `<tr><td>Notes</td><td>${txn.notes}</td></tr>` : ''}
        <tr class="amount-row"><td>Amount</td><td>${formatCurrency(txn.amount)}</td></tr>
      </table>
      <div class="footer">Thank you for your payment</div>
    </body></html>`;

  const win = window.open('', '_blank', 'width=480,height=640');
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); win.close(); }, 300);
}

function CustomerPayments() {
  const { isCompanyAdmin } = useAuth();
  const [formData, setFormData] = useState({
    transactionType: 'INCOME',
    transactionDate: new Date().toISOString().split('T')[0],
    branchId: '',
    customerId: '',
    projectId: '',
    accountHeadId: '',
    paymentAccountHeadId: '',
    paymentMode: 'CASH',
    amount: '',
    referenceNo: '',
    notes: ''
  });

  const [branches, setBranches]       = useState([]);
  const [branchLogoUrl, setBranchLogoUrl] = useState(null);
  const [customers, setCustomers]     = useState([]);
  const [projects, setProjects]       = useState([]);
  const [accountHeads, setAccountHeads] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [editingTxn, setEditingTxn]         = useState(null);
  const [formErrors, setFormErrors]         = useState({});
  const [loading, setLoading]               = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [historyTxn, setHistoryTxn] = useState(null);
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (historyTxn) {
      fetchHistory(historyTxn.id);
    }
  }, [historyTxn]);

  const fetchHistory = async (txnId) => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`/financial-transactions/${txnId}/approval-history`);
      setHistoryList(res.data?.data || []);
    } catch (err) {
      toast.error('Failed to load approval history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const renderStatus = (txn) => {
    const status = txn.status || 'APPROVED';
    let bg = '#e2e8f0', color = '#475569', label = status;
    if (status === 'APPROVED') { bg = '#dcfce7'; color = '#15803d'; label = 'Approved'; }
    else if (status === 'REJECTED') { bg = '#fee2e2'; color = '#b91c1c'; label = 'Rejected'; }
    else if (status === 'PENDING_L1') { bg = '#fef3c7'; color = '#d97706'; label = 'Pending L1'; }
    else if (status === 'PENDING_L2') { bg = '#f3e8ff'; color = '#7e22ce'; label = 'Pending L2'; }
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
        <span 
          style={{ 
            backgroundColor: bg, 
            color: color, 
            padding: '2px 8px', 
            borderRadius: '4px', 
            fontSize: '11px', 
            fontWeight: '600',
            textTransform: 'uppercase',
            display: 'inline-block'
          }}
        >
          {label}
        </span>
        <button
          type="button"
          onClick={() => setHistoryTxn(txn)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '14px' }}
          title="View Approval History"
        >
          ℹ️
        </button>
      </div>
    );
  };

  const renderHistoryModal = () => {
    if (!historyTxn) return null;
    return (
      <div className="modal-overlay" style={{ zIndex: 1200, display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }} onClick={() => setHistoryTxn(null)}>
        <div className="modal-content" style={{ maxWidth: '500px', background: 'white', borderRadius: '8px', width: '90%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
          <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold' }}>Approval History - {historyTxn.voucherNo}</h2>
            <button type="button" className="close-button" onClick={() => setHistoryTxn(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
          </div>
          <div className="modal-body" style={{ padding: '20px', maxHeight: '400px', overflowY: 'auto' }}>
            {historyLoading ? (
              <div>Loading history...</div>
            ) : historyList.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>No history logs found (Bypassed or legacy transaction).</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {historyList.map((h, i) => {
                  let actColor = 'var(--text-main)';
                  if (h.action?.includes('APPROVE')) actColor = 'green';
                  if (h.action?.includes('REJECT')) actColor = 'red';
                  return (
                    <div key={h.id} style={{ display: 'flex', gap: '12px', borderBottom: i < historyList.length - 1 ? '1px solid #eee' : 'none', paddingBottom: '10px' }}>
                      <div style={{ fontSize: '18px' }}>
                        {h.action?.includes('APPROVE') ? '✅' : h.action?.includes('REJECT') ? '❌' : '📤'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: '600', color: actColor }}>{h.action}</span>
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{new Date(h.performedAt).toLocaleString()}</span>
                        </div>
                        <div style={{ fontSize: '13px', marginTop: '2px' }}>
                          By: <strong>{h.performedByName}</strong> ({h.performedByRole})
                        </div>
                        {h.remarks && (
                          <div style={{ fontSize: '13px', background: '#f8f9fa', padding: '6px', borderRadius: '4px', marginTop: '4px', color: '#555', fontStyle: 'italic' }}>
                            Remarks: "{h.remarks}"
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={() => setHistoryTxn(null)}>Close</button>
          </div>
        </div>
      </div>
    );
  };
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [listSearch, setListSearch] = useState('');
  const [viewingTxn, setViewingTxn] = useState(null);
  const [showNewAccountHeadModal, setShowNewAccountHeadModal] = useState(false);
  const [newAccountHeadForm, setNewAccountHeadForm] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    if (isCompanyAdmin()) fetchBranches();
    fetchCustomers();
    fetchAccountHeads();
    fetchPaymentAccounts();
    fetchRecentPayments();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await axios.get('/company/branches');
      const data = response.data.data || response.data;
      const list = Array.isArray(data) ? data : [];
      setBranches(list);
      // Use the first branch logo found (or the user's own branch)
      const withLogo = list.find(b => b.logoUrl);
      if (withLogo) {
        setBranchLogoUrl(`${config.apiBaseUrl}${withLogo.logoUrl}`);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/customers?size=1000');
      const data = response.data.data?.content || response.data.content || response.data.data || [];
      setCustomers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchProjects = useCallback(async (customerId) => {
    if (!customerId) { setProjects([]); return; }
    try {
      const response = await axios.get(`/projects/by-customer/${customerId}`);
      const data = response.data.data || response.data || [];
      setProjects(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    }
  }, []);

  const fetchAccountHeads = async () => {
    try {
      const response = await axios.get('/account-heads');
      const data = response.data.data || response.data || [];
      const incomeHeads = (Array.isArray(data) ? data : []).filter(h => h.category === 'INCOME');
      setAccountHeads(incomeHeads);
    } catch (error) {
      console.error('Error fetching account heads:', error);
    }
  };

  const fetchPaymentAccounts = async () => {
    try {
      const response = await axios.get('/account-heads/category/PAYMENT_ACCOUNT');
      const data = response.data.data || response.data || [];
      setPaymentAccounts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching payment accounts:', error);
    }
  };

  const fetchRecentPayments = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await axios.get('/financial-transactions/type/INCOME');
      const data = response.data.data || response.data || [];
      setRecentPayments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching recent payments:', error);
    } finally {
      setListLoading(false);
    }
  }, []);

  const handleCreateAccountHead = async () => {
    if (!newAccountHeadForm.name.trim()) return;
    try {
      const response = await axios.post('/account-heads', {
        name: newAccountHeadForm.name.trim(),
        description: newAccountHeadForm.description.trim(),
        category: 'INCOME',
        active: true
      });
      const data = response.data?.data || response.data;
      if (data && data.id) {
        await fetchAccountHeads();
        setFormData(prev => ({
          ...prev,
          accountHeadId: String(data.id)
        }));
      }
      setShowNewAccountHeadModal(false);
      setNewAccountHeadForm({ name: '', description: '' });
    } catch (err) {
      console.error('Failed to create account head:', err);
      alert(err.response?.data?.message || 'Failed to create account head');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'accountHeadId' && value === 'ADD_NEW') {
      setShowNewAccountHeadModal(true);
      setFormData(prev => ({ ...prev, accountHeadId: '' }));
      return;
    }
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'paymentAccountHeadId') {
        const selected = paymentAccounts.find(a => String(a.id) === String(value));
        const selName = selected?.name?.toLowerCase();
        if (selName === 'cash in hand' || selName === 'petty cash') {
          updated.paymentMode = 'CASH';
        } else {
          updated.paymentMode = 'BANK';
        }
      }
      // When customer changes, reload projects and clear project selection
      if (name === 'customerId') {
        updated.projectId = '';
        fetchProjects(value);
      }
      // Update logo when branch is selected
      if (name === 'branchId' && value) {
        const selected = branches.find(b => String(b.id) === value);
         if (selected?.logoUrl) setBranchLogoUrl(`${config.apiBaseUrl}${selected.logoUrl}`);
      }
      return updated;
    });
    if (formErrors?.[name]) setFormErrors?.(p => ({ ...p, [name]: '' }));
  };

  const handleEdit = (txn) => {
    setEditingTxn(txn);
    if (txn.customerId) fetchProjects(String(txn.customerId));
    setFormData({
      transactionType: 'INCOME',
      transactionDate: txn.transactionDate || new Date().toISOString().split('T')[0],
      branchId:              txn.branchId              ? String(txn.branchId)              : '',
      customerId:            txn.customerId            ? String(txn.customerId)            : '',
      projectId:             txn.projectId             ? String(txn.projectId)             : '',
      accountHeadId:         txn.accountHeadId         ? String(txn.accountHeadId)         : '',
      paymentAccountHeadId:  txn.paymentAccountHeadId  ? String(txn.paymentAccountHeadId)  : '',
      paymentMode:           txn.paymentMode           || 'CASH',
      amount:                txn.amount                ? String(txn.amount)                : '',
      referenceNo:           txn.referenceNo           || '',
      notes:                 txn.notes                 || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingTxn(null);
    setFormData({
      transactionType: 'INCOME',
      transactionDate: new Date().toISOString().split('T')[0],
      branchId: '', customerId: '', projectId: '', accountHeadId: '',
      paymentAccountHeadId: '', paymentMode: 'CASH', amount: '', referenceNo: '', notes: ''
    });
    setCustomerSearch('');
  };

  const handleSubmit = async (e, shouldPrint = false) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        branchId:             formData.branchId             ? parseInt(formData.branchId)             : null,
        customerId:           formData.customerId           ? parseInt(formData.customerId)           : null,
        projectId:            formData.projectId            ? parseInt(formData.projectId)            : null,
        accountHeadId:        formData.accountHeadId        ? parseInt(formData.accountHeadId)        : null,
        paymentAccountHeadId: formData.paymentAccountHeadId ? parseInt(formData.paymentAccountHeadId) : null,
        amount:               String(formData.amount)
      };

      if (isCompanyAdmin() && !payload.branchId && !editingTxn) {
        toast.error('Please select a branch');
        setLoading(false);
        return;
      }

      let txn;
      if (editingTxn) {
        const res = await axios.put(`/financial-transactions/${editingTxn.id}`, payload);
        txn = res.data.data || res.data;
        toast.success(`Updated! Voucher: ${txn.voucherNo}`);
        setEditingTxn(null);
      } else {
        const res = await axios.post('/financial-transactions', payload);
        txn = res.data.data || res.data;
        toast.success(`Payment recorded! Voucher: ${txn.voucherNo}`);
      }

      // Enrich with names for the receipt
      const customer = customers.find(c => c.id === payload.customerId);
      const project  = projects.find(p => p.id === payload.projectId);
      const head     = accountHeads.find(h => h.id === payload.accountHeadId);
      const enriched = {
        ...txn,
        customerName:    customer?.name    || txn.customerName,
        projectName:     project?.name     || txn.projectName,
        accountHeadName: head?.name        || txn.accountHeadName,
        paymentMode:     formData.paymentMode,
        referenceNo:     formData.referenceNo,
        notes:           formData.notes,
      };

      setFormData({
        transactionType: 'INCOME',
        transactionDate: new Date().toISOString().split('T')[0],
        branchId: '', customerId: '', projectId: '', accountHeadId: '',
        paymentAccountHeadId: '', paymentMode: 'CASH', amount: '', referenceNo: '', notes: ''
      });
      setCustomerSearch('');
      fetchRecentPayments();

      if (shouldPrint) {
        printReceipt(enriched, branchLogoUrl);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const selectedAccount = paymentAccounts.find(a => String(a.id) === String(formData.paymentAccountHeadId));
  const selectedAccountName = selectedAccount?.name?.toLowerCase();
  const isCashOrPetty = selectedAccountName === 'cash in hand' || selectedAccountName === 'petty cash';

  return (
    <div className="financial-transactions">
      <h1>Receipts</h1>
      <p>Record customer receipts and payments</p>

      {/* Centered stacked layout wrapper */}
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* ── Form ── */}
        <div className="transaction-form-container">
          <h2 className="section-title">{editingTxn ? `Editing ${editingTxn.voucherNo}` : 'New Payment'}</h2>
          <form onSubmit={handleSubmit} className="transaction-form" style={{ maxWidth: '100%' }}>

            {isCompanyAdmin() && (
              <div className="form-row">
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Branch *</label>
                  <select name="branchId" value={formData.branchId} onChange={handleChange} required={isCompanyAdmin()}>
                    <option value="">Select Branch</option>
                    {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Transaction Date *</label>
                <input type="date" name="transactionDate" value={formData.transactionDate} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Payment Account *</label>
                <select name="paymentAccountHeadId" value={formData.paymentAccountHeadId} onChange={handleChange} required>
                  <option value="">Select Account</option>
                  {paymentAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            </div>

            <div className="form-row">
              {!isCashOrPetty && (
                <div className="form-group">
                  <label>Payment Mode</label>
                  <select name="paymentMode" value={formData.paymentMode} onChange={handleChange}>
                    <option value="BANK">Bank Transfer</option>
                    <option value="UPI">UPI</option>
                    <option value="CHEQUE">Cheque</option>
                  </select>
                </div>
              )}
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Customer *</label>
                <input
                  type="text"
                  placeholder="Type to search & select customer..."
                  value={customerDropdownOpen ? customerSearch : (customers.find(c => String(c.id) === formData.customerId)?.name || '')}
                  onFocus={() => {
                    setCustomerSearch('');
                    setCustomerDropdownOpen(true);
                  }}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  onBlur={() => setTimeout(() => setCustomerDropdownOpen(false), 200)}
                  required
                  style={{ cursor: 'pointer' }}
                />
                {customerDropdownOpen && (
                  <div className="custom-select-dropdown" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    background: 'var(--bg-card, #ffffff)',
                    border: '1px solid var(--border, #ddd)',
                    borderRadius: '6px',
                    boxShadow: 'var(--shadow, 0 4px 12px rgba(0,0,0,0.1))',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    marginTop: '4px'
                  }}>
                    {customers
                      .filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase()))
                      .map(c => (
                        <div
                          key={c.id}
                          className="custom-select-option"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setFormData(prev => {
                              const updated = { ...prev, customerId: String(c.id), projectId: '' };
                              fetchProjects(String(c.id));
                              return updated;
                            });
                            setCustomerDropdownOpen(false);
                            setCustomerSearch('');
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            borderBottom: '1px solid var(--border, #f0f0f0)',
                            backgroundColor: String(c.id) === formData.customerId ? 'var(--menu-active-bg, #e8f4ff)' : 'transparent',
                            color: String(c.id) === formData.customerId ? 'var(--menu-active-text, #0056b3)' : 'var(--text-primary, inherit)'
                          }}
                          onMouseEnter={(e) => {
                            if (String(c.id) !== formData.customerId) e.target.style.backgroundColor = 'var(--border, #f5f5f5)';
                          }}
                          onMouseLeave={(e) => {
                            if (String(c.id) !== formData.customerId) e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          {c.name}
                        </div>
                      ))
                    }
                    {customers.filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase())).length === 0 && (
                      <div style={{ padding: '8px 12px', color: 'var(--text-secondary, #999)', fontSize: '13px', textAlign: 'center' }}>
                        No customers found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Project *</label>
                <select name="projectId" value={formData.projectId} onChange={handleChange} required>
                  <option value="">
                    {formData.customerId ? 'Select Project' : 'Select Customer first'}
                  </option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Income Head *</label>
                <select name="accountHeadId" value={formData.accountHeadId} onChange={handleChange} required>
                  <option value="">Select Income Head</option>
                  {accountHeads.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                  <option value="ADD_NEW">+ Add New Income Head</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Amount *</label>
              <input type="number" name="amount" value={formData.amount} onChange={handleChange}
                placeholder="0.00" step="0.01" min="0.01" required />
            </div>

            <div className="form-group">
              <label>Reference Number (UTR / Cheque No)</label>
              <input type="text" name="referenceNo" value={formData.referenceNo} onChange={handleChange}
                placeholder="Enter reference number" />
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea name="notes" value={formData.notes} onChange={handleChange}
                placeholder="Additional notes..." rows="3" />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Saving…' : editingTxn ? 'Update Payment' : 'Record Payment'}
              </button>
              {!editingTxn && (
                <button
                  type="button"
                  className="btn-primary btn-print-save"
                  disabled={loading}
                  onClick={(e) => {
                    const form = e.target.closest('form');
                    if (form.checkValidity()) handleSubmit(e, true);
                    else form.reportValidity();
                  }}
                >
                  {loading ? 'Saving…' : '🖨 Record & Print'}
                </button>
              )}
              {editingTxn && (
                <button type="button" className="btn-secondary" onClick={handleCancelEdit}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* ── Recent Payments Table ── */}
        <div className="transaction-form-container" style={{ marginTop: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <h2 className="section-title" style={{ margin: 0, border: 'none', padding: 0 }}>Recent Receipts & Payments</h2>
            <input
              type="text"
              placeholder="🔍 Search by customer or voucher..."
              value={listSearch}
              onChange={(e) => setListSearch(e.target.value)}
              style={{ width: '300px', padding: '8px 12px', fontSize: '13.5px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box' }}
            />
          </div>

          {listLoading ? (
            <div className="loading">Loading…</div>
          ) : recentPayments.filter(txn => {
              const cust = (txn.customerName || '').toLowerCase();
              const vch = (txn.voucherNo || '').toLowerCase();
              const q = listSearch.toLowerCase();
              return cust.includes(q) || vch.includes(q);
            }).length === 0 ? (
            <div className="no-data-panel" style={{ textAlign: 'center', padding: '30px' }}>No matching payments found.</div>
          ) : (
            <div className="table-responsive" style={{ overflowX: 'auto' }}>
              <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-card-alt)', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                    <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Voucher No</th>
                    <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Date</th>
                    <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Customer & Project</th>
                    <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Payment Mode</th>
                    <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Reference No</th>
                    <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', textAlign: 'right' }}>Amount</th>
                    <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', textAlign: 'center' }}>Status</th>
                    <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentPayments
                    .filter(txn => {
                      const cust = (txn.customerName || '').toLowerCase();
                      const vch = (txn.voucherNo || '').toLowerCase();
                      const q = listSearch.toLowerCase();
                      return cust.includes(q) || vch.includes(q);
                    })
                    .map(txn => (
                      <tr key={txn.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px', fontFamily: 'monospace', fontWeight: 'bold' }}>{txn.voucherNo}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px' }}>{formatDate(txn.transactionDate)}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px' }}>
                          <div style={{ fontWeight: '500' }}>{txn.customerName || '—'}</div>
                          {txn.projectName && <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{txn.projectName}</div>}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px' }}>
                          <div>{MODE_LABELS[txn.paymentMode] || txn.paymentMode || '—'}</div>
                          {txn.paymentAccountHeadName && <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{txn.paymentAccountHeadName}</div>}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px', color: 'var(--text-secondary)' }}>{txn.referenceNo || '—'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px', fontWeight: 'bold', textAlign: 'right', color: 'var(--accent)' }}>{formatCurrency(txn.amount)}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px', textAlign: 'center' }}>{renderStatus(txn)}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                              onClick={() => setViewingTxn(txn)}
                              title="View Details"
                            >
                              👁 View
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                              onClick={() => handleEdit(txn)}
                              disabled={txn.status === 'PENDING_L1' || txn.status === 'PENDING_L2'}
                              title={txn.status?.startsWith('PENDING') ? 'Pending approval' : 'Edit'}
                            >
                              ✏️ Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── View Receipt Details Modal ── */}
        {viewingTxn && (
          <div className="modal-overlay" style={{ zIndex: 1100, display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }} onClick={() => setViewingTxn(null)}>
            <div className="modal-content" style={{ maxWidth: '500px', background: 'white', borderRadius: '8px', width: '90%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
              <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold' }}>Receipt Details - {viewingTxn.voucherNo}</h2>
                <button type="button" className="close-button" onClick={() => setViewingTxn(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
              </div>
              <div className="modal-body" style={{ padding: '24px', overflowY: 'auto' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                    <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Voucher No</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{viewingTxn.voucherNo}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                    <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Date</span>
                    <span>{formatDate(viewingTxn.transactionDate)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                    <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Customer</span>
                    <span>{viewingTxn.customerName || '—'}</span>
                  </div>
                  {viewingTxn.projectName && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Project</span>
                      <span>{viewingTxn.projectName}</span>
                    </div>
                  )}
                  {viewingTxn.paymentAccountHeadName && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Payment Account</span>
                      <span>{viewingTxn.paymentAccountHeadName}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                    <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Payment Mode</span>
                    <span>{MODE_LABELS[viewingTxn.paymentMode] || viewingTxn.paymentMode || '—'}</span>
                  </div>
                  {viewingTxn.referenceNo && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Reference No</span>
                      <span>{viewingTxn.referenceNo}</span>
                    </div>
                  )}
                  {viewingTxn.notes && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Notes</span>
                      <span style={{ color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{viewingTxn.notes}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '1.2rem', color: 'var(--text-primary)' }}>Amount</span>
                    <span style={{ fontWeight: 'bold', fontSize: '1.2rem', color: 'var(--accent)' }}>{formatCurrency(viewingTxn.amount)}</span>
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ padding: '20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '10px', background: '#ffffff' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => printReceipt(viewingTxn, branchLogoUrl)}
                >
                  🖨 Print
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setViewingTxn(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

      {showNewAccountHeadModal && (
        <div
          className="modal-overlay"
          style={{ zIndex: 1100 }}
          onClick={(e) => {
            e.stopPropagation();
            setShowNewAccountHeadModal(false);
            setNewAccountHeadForm({ name: '', description: '' });
          }}
        >
          <div
            className="modal-content"
            style={{ maxWidth: '450px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>New Income Head</h2>
              <button
                type="button"
                className="close-button"
                onClick={() => {
                  setShowNewAccountHeadModal(false);
                  setNewAccountHeadForm({ name: '', description: '' });
                }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Name *</label>
                <input
                  type="text"
                  value={newAccountHeadForm.name}
                  onChange={(e) => setNewAccountHeadForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., Progress Billing"
                  required
                />
              </div>
              <div className="form-group" style={{ marginTop: '14px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Description</label>
                <textarea
                  value={newAccountHeadForm.description}
                  onChange={(e) => setNewAccountHeadForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Describe this income head..."
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '20px', borderTop: '1px solid var(--border, #e5e7eb)', background: '#ffffff', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowNewAccountHeadModal(false);
                  setNewAccountHeadForm({ name: '', description: '' });
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateAccountHead}
                disabled={!newAccountHeadForm.name.trim()}
              >
                Save Income Head
              </button>
            </div>
          </div>
        </div>
      )}
      {renderHistoryModal()}
    </div>
  </div>
  );
}

export default CustomerPayments;
